import React from 'react';
import { Text, Divider } from 'wix-ui-tpa/cssVars';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { isFreePlan } from '@wix/pricing-plans-utils';
import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import { getOrderCoupon, getPricesCoupon, hasOrderCoupon } from '../../../../../utils/domainUtils';
import { isStartDateCustomizable } from '../../../../../utils/plan';
import { CouponInput } from '../CouponInput';
import { DateInput } from './DateInput';
import { CheckoutSummaryPresenter } from './presenters';
import { classes } from './Small.st.css';
import { SummaryProps } from './types';
import { useOrderSummary } from './useOrderSummary';

export const SmallSummary: React.FC<SummaryProps> = ({
  locale,
  plan,
  prices = [],
  benefits,
  order,
  startDate,
  setStartDate,
  minStartDate,
  maxStartDate,
  isStartDateDisabled,
  updatePriceDetails,
  updatePriceDetailsError,
  couponInputMode,
  couponLoading,
  couponCode,
  removeCoupon,
  applyCouponError,
  hasCoupons,
}) => {
  const isCouponsEnabled = hasCoupons && !isFreePlan(plan);
  const { t } = useTranslation();
  const presenter = new CheckoutSummaryPresenter(plan, prices, benefits, locale, t, order);
  const orderSummary = useOrderSummary(plan, prices);
  const { total, subtotal, taxAmount, couponAmount } = orderSummary;
  const hasFreeTrial = presenter.showFreeTrial();
  const taxName = presenter.getTaxName();
  const taxRate = presenter.getTaxRate();
  const paymentDisclaimer = presenter.getPaymentDisclaimer();
  const hasCouponOnOrder = order && hasOrderCoupon(order);
  const couponId = hasCouponOnOrder ? getOrderCoupon(order!)?.id : getPricesCoupon(prices)?.id;
  const { experiments } = useExperiments();
  const shouldLocalizeSessionCount = experiments.enabled(TPA_EXPERIMENTS.NUMBER_LOCALIZATION);

  return (
    <div className={classes.box}>
      <div className={classes.section}>
        <Text className={classes.heading} tagName="h2">
          {t('payment.checkout-summary.heading')}
        </Text>
        {isStartDateCustomizable(plan) ? (
          <DateInput
            label={t('payment.mini-checkout-summary.start-date')}
            disabled={isStartDateDisabled}
            dataHook="small-start-date-input"
            value={startDate}
            max={maxStartDate}
            min={minStartDate}
            onChange={setStartDate}
          />
        ) : null}
      </div>
      <div className={classes.section}>
        <div className={classes.entry}>
          <Text className={classes.label}>{t('payment.checkout-summary.plan')}</Text>
          <Text className={classes.value} data-hook="payment-checkout-summary-plan-name">
            {presenter.getPlanName()}
          </Text>
        </div>
        <div className={classes.entry}>
          <Text className={classes.label}>{t('payment.checkout-summary.duration')}</Text>
          <Text className={classes.value} data-hook="payment-checkout-small-summary-duration">
            {presenter.getDuration()}
          </Text>
        </div>
        {presenter.hasSessions() ? (
          <div className={classes.entry}>
            <Text className={classes.label}>{t('payment.checkout-summary.sessions')}</Text>
            <Text className={classes.value} data-hook="payment-checkout-small-summary-sessions">
              {presenter.getSessionCount(shouldLocalizeSessionCount)}
            </Text>
          </div>
        ) : null}
        {hasFreeTrial ? (
          <div className={classes.entry} data-hook="payment-checkout-small-summary-plan-free-trial">
            <Text className={classes.label} data-hook="payment-checkout-small-summary-plan-free-trial-label">
              {t('payment.checkout-summary.trial')}
            </Text>
            <Text className={classes.value} data-hook="payment-checkout-small-summary-free-trial-days">
              {presenter.getFreeTrialDays()}
            </Text>
          </div>
        ) : null}
        {hasFreeTrial && presenter.isFreeTrialUnavailable() ? (
          <div className={classes.freeTrialDisclaimerEntry}>
            <Text className={classes.freeTrialDisclaimer}>
              {t('payment.checkout-summary.trial-unavailable-disclaimer')}
            </Text>
          </div>
        ) : null}
      </div>
      {isCouponsEnabled ? (
        <>
          <Divider className={classes.divider} />
          <CouponInput
            disabled={hasCouponOnOrder || isStartDateDisabled}
            mode={hasCouponOnOrder ? 'coupon' : couponInputMode}
            error={updatePriceDetailsError ?? applyCouponError}
            value={hasCouponOnOrder ? getOrderCoupon(order!)!.code! : couponCode}
            onApplyClick={(code) => updatePriceDetails(plan.id!, code)}
            onRemoveClick={() => removeCoupon(plan.id!, couponId!)}
            loading={couponLoading}
          />
        </>
      ) : null}
      {((taxAmount || presenter.hasCoupon()) && subtotal) ||
      (presenter.hasCoupon() && subtotal) ||
      (taxAmount && subtotal) ? (
        <>
          <Divider className={classes.divider} />
          <div className={classes.section}>
            {(taxAmount || presenter.hasCoupon()) && subtotal ? (
              <div className={classes.entry} data-hook="payment-checkout-small-summary-subtotal-row">
                <Text className={classes.label} data-hook="payment-checkout-small-summary-subtotal-label">
                  {t('payment.checkout-summary.subtotal')}
                </Text>
                <Text className={classes.value} data-hook="payment-checkout-small-summary-subtotal">
                  {subtotal}
                </Text>
              </div>
            ) : null}
            {presenter.hasCoupon() && subtotal ? (
              <div className={classes.entry} data-hook="payment-checkout-small-summary-coupon-discount-row">
                <Text className={classes.label} data-hook="payment-checkout-small-summary-coupon-discount-label">
                  {t('payment.checkout-summary.coupon-code')}
                </Text>
                <Text className={classes.value} data-hook="payment-checkout-small-summary-coupon-discount">
                  {couponAmount}
                </Text>
              </div>
            ) : null}
            {taxAmount && subtotal ? (
              <div className={classes.entry} data-hook="payment-checkout-small-summary-tax-row">
                <Text className={classes.label} data-hook="payment-checkout-small-summary-tax-label">
                  {taxName} ({taxRate} %)
                </Text>
                <Text className={classes.value} data-hook="payment-checkout-small-summary-tax">
                  {taxAmount}
                </Text>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
      <Divider className={classes.divider} />
      <div className={classes.section}>
        <div className={classes.entry}>
          <Text className={classes.labelLarge} data-hook="payment-checkout-small-summary-plan-total">
            {t('payment.checkout-summary.total')}
          </Text>
          <Text className={classes.valueLarge} data-hook="payment-checkout-small-summary-price">
            {total}
          </Text>
        </div>
        {presenter.isRecurring() ? (
          <div className={classes.paymentPeriodEntry}>
            <Text className={classes.paymentPeriod}>{presenter.getPaymentPeriod()}</Text>
          </div>
        ) : null}
      </div>
      {paymentDisclaimer ? (
        <div className={classes.section}>
          <div className={classes.entry}>
            <Text className={classes.value}>{paymentDisclaimer}</Text>
          </div>
        </div>
      ) : null}
    </div>
  );
};
